/**
 * /approvals — every pipeline run in this tenant that is waiting on a person.
 *
 * Not filtered to the caller. A gate you cannot decide is still a migration
 * that is stopped, and an inbox that hides it would leave a run parked with no
 * sign of why. Each card says whether *you* may decide it and, when you may
 * not, the reason — computed server-side by the same function the endpoint
 * enforces with, so the disabled button and the refusal cannot disagree.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Group,
  Loader,
  Stack,
  Text,
  Textarea,
  Title,
  Tooltip,
} from "@mantine/core";
import { Link as RouterLink } from "react-router-dom";

import { useAccessToken } from "@/features/auth/useAccessToken";
import { decideApproval, listPendingApprovals, type ApprovalOut } from "@/shared/api/approvals";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";

function whoAsked(a: ApprovalOut): string {
  return a.requested_by_name || a.requested_by_email || a.requested_by_user_id || "someone";
}

/** The one-line summary of what reached the gate, from the node above it. */
function contextLine(context: Record<string, unknown> | null | undefined): string | null {
  if (!context) return null;
  const kind = String(context.kind ?? "");
  if (kind === "rows") {
    const n = Number(context.row_count ?? 0);
    return `${n.toLocaleString()} ${n === 1 ? "row" : "rows"} reached this step`;
  }
  if (kind === "summary") {
    const status = context.status ? String(context.status) : null;
    const name = context.pipeline ? String(context.pipeline) : null;
    if (name && status) return `${name} ${status}`;
  }
  return null;
}

export function ApprovalCard({
  approval,
  onDecided,
}: {
  approval: ApprovalOut;
  onDecided: () => void;
}) {
  const getToken = useAccessToken();
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState<"approve" | "reject" | null>(null);
  const summary = contextLine(approval.context as Record<string, unknown> | null);

  const decide = async (approved: boolean) => {
    setBusy(approved ? "approve" : "reject");
    try {
      await decideApproval(await getToken(), approval.approval_id, { approved, comment });
      toastSuccess(
        approved ? "Approved" : "Rejected",
        approved
          ? `${approval.pipeline_name ?? "The run"} carries on from here.`
          : `${approval.pipeline_name ?? "The run"} stops at this gate.`,
      );
      onDecided();
    } catch (err) {
      toastApiError(approved ? "Could not approve" : "Could not reject", err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card withBorder radius="md" padding="md">
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <div>
            <Text fw={600}>{approval.title}</Text>
            <Text size="sm" c="dimmed">
              {approval.project_name ? `${approval.project_name} · ` : ""}
              <RouterLink
                to={`/projects/${approval.project_id ?? ""}/pipelines/${approval.pipeline_id}`}
              >
                {approval.pipeline_name ?? approval.pipeline_id}
              </RouterLink>{" "}
              · asked by {whoAsked(approval)}
            </Text>
          </div>
          <Group gap="xs" wrap="nowrap">
            {approval.lifecycle_state && (
              <Badge
                variant="light"
                color={approval.lifecycle_state === "development" ? "gray" : "orange"}
              >
                {approval.lifecycle_state}
              </Badge>
            )}
            <Badge variant="outline">{approval.required_role}</Badge>
          </Group>
        </Group>

        {approval.instructions && (
          <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
            {approval.instructions}
          </Text>
        )}
        {summary && (
          <Text size="sm" c="dimmed">
            {summary}
          </Text>
        )}

        {approval.can_decide ? (
          <>
            <Textarea
              label="Comment"
              description="Required if you reject — it is what the person fixing this will read."
              value={comment}
              onChange={(e) => setComment(e.currentTarget.value)}
              autosize
              minRows={2}
              maxLength={1000}
            />
            <Group justify="flex-end">
              <Button
                variant="default"
                color="red"
                loading={busy === "reject"}
                disabled={busy !== null || comment.trim().length === 0}
                onClick={() => void decide(false)}
              >
                Reject
              </Button>
              <Button
                loading={busy === "approve"}
                disabled={busy !== null}
                onClick={() => void decide(true)}
              >
                Approve
              </Button>
            </Group>
          </>
        ) : (
          <Alert color="gray" variant="light">
            {approval.cannot_decide_reason ?? "You may not decide this one."}
          </Alert>
        )}

        <Text size="xs" c="dimmed">
          Run <Code>{approval.run_id}</Code> · waiting since{" "}
          {new Date(approval.requested_at_utc).toLocaleString()}
        </Text>
      </Stack>
    </Card>
  );
}

export function ApprovalsInbox() {
  const getToken = useAccessToken();
  const [items, setItems] = useState<ApprovalOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setItems((await listPendingApprovals(await getToken())).items);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setItems([]);
    }
  }, [getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const mine = (items ?? []).filter((a) => a.can_decide);
  const others = (items ?? []).filter((a) => !a.can_decide);

  return (
    <Stack gap="lg">
      {/* No heading — this renders inside the Projects hub, whose tab already
          names it. */}
      <Text c="dimmed">
        Pipeline runs that have stopped at a gate and are waiting for a person. Oldest first — the
        one that has been waiting longest is the one costing the most.
      </Text>

      {error && <Alert color="red">{error}</Alert>}
      {items === null && <Loader />}
      {items !== null && items.length === 0 && !error && (
        <Alert color="gray" variant="light">
          Nothing is waiting. Runs appear here when they reach an Approval node.
        </Alert>
      )}

      {mine.length > 0 && (
        <Stack gap="sm">
          <Title order={4}>Waiting for you</Title>
          {mine.map((a) => (
            <ApprovalCard key={a.approval_id} approval={a} onDecided={() => void load()} />
          ))}
        </Stack>
      )}

      {others.length > 0 && (
        <Stack gap="sm">
          <Group gap="xs">
            <Title order={4}>Waiting for someone else</Title>
            <Tooltip label="Shown so a stopped migration is never invisible — you just cannot decide these.">
              <Badge variant="light" color="gray">
                {others.length}
              </Badge>
            </Tooltip>
          </Group>
          {others.map((a) => (
            <ApprovalCard key={a.approval_id} approval={a} onDecided={() => void load()} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}
